import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import Compressor from 'compressorjs';

import styles from './QrCodePhotos.module.scss';
import trashIcon from "../../constants/images/trash-icon.svg";
import ModalPreviewSelectedPhoto from "../ModalPreviewSelectedPhoto/ModalPreviewSelectedPhoto";
import {qrCodeActions} from "../../store/slices/qrCodeSlice";
import {modalPreviewSelectedPhotoActions} from "../../store/slices/modalPreviewSelectedPhotoSlice";
import MainButtonWithBackground from "../UI/MainButtonWithBackground/MainButtonWithBackground";
import {qrCodeService} from "../../services/qrCode.service";
import {MAIN_ROUTES} from "../../routing/main_routes";
import ErrorPage from "../../pages/ErrorPage/ErrorPage";
import Loader from "../Loader/Loader";
import toast, {Toaster} from "react-hot-toast";
import {convertHeicList} from "../../utils/heicConvert";

const QrCodePhotos = () => {
  const {pathname} = useLocation();
  const array = pathname.split('/');

  let sessionId;
  if (pathname.endsWith('/')) {
    sessionId = array[array.length - 2];
  } else {
    sessionId = array[array.length - 1];
  }

  const [isPhotos, setIsPhotos] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const {
    uploadedPhotosFromDevice,
    compressedFilesFromDevice
  } = useSelector(state => state.qrCode);
  const {isPreviewOpen} = useSelector(state => state.modalPreviewSelectedPhoto);

  const [photoForPreview, setPhotoForPreview] = useState(null);

  const handleAddPhoto = () => {
    document.getElementById('fileInput').click();
  };

  const navigate = useNavigate();

  useEffect(() => {
    const getPhotosByQrSessionId = async () => {
      try {
        const res = await qrCodeService.getPhotos(sessionId);
        if (res?.photos?.length > 0) {
          setIsPhotos(true);
        }
      } catch (error) {
        console.error(error);
      }
    }

    getPhotosByQrSessionId();
  }, [])


  const handleGetPhotosFromInput = async (e) => {
    // iPhone shoots HEIC; canvas compressors and the backend can't decode it,
    // so convert HEIC->JPEG up front or the photo silently vanishes from the
    // order. Everything below works off the converted list.
    const sourceFiles = await convertHeicList(e.target.files);

    const filesWithUniqueNames = sourceFiles.map((file, index) => {
      const extension = file.name.split('.').pop();
      const nameWithoutExtension = file.name.replace(`.${extension}`, '');
      const uniqueName = `${nameWithoutExtension}-${Date.now()}-${index}.${extension}`;
      return new File([file], uniqueName, {type: file.type});
    });

    // Compress best-effort while preserving selection order. A compression
    // failure must NOT reject - that would reject the whole batch and silently
    // drop every selected photo - so on error we fall back to the original file.
    const compressedFiles = await Promise.all(filesWithUniqueNames.map((file) =>
      new Promise((resolve) => {
        new Compressor(file, {
          quality: 0.6,
          success: (compressedResult) => {
            resolve(new File([compressedResult], file.name, {type: compressedResult.type}));
          },
          error: (err) => {
            console.error('Compression failed:', err);
            resolve(file);
          },
        });
      })
    ));

    const totalSize = [...compressedFiles, ...compressedFilesFromDevice].reduce((acc, file) => acc + file.size, 0);

    const maxSizeForDb = 20 * 1024 * 1024; // 20 MB

    if (totalSize < maxSizeForDb) {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

      const files = sourceFiles.filter(file => {
        return allowedTypes.includes(file.type);
      });

      if (files.length !== sourceFiles.length) {
        toast.error("Дозволені тільки файли у форматах PNG, JPEG та JPG", {id: "qr-code", duration: 5000});
        return;
      }

      const urls = sourceFiles.map(file => ({
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file),
      }));

      if (compressedFiles?.length > 0) {
        dispatch(qrCodeActions.setCompressedFilesFromDevice([...compressedFilesFromDevice, ...compressedFiles]));
      } else {
        dispatch(qrCodeActions.setCompressedFilesFromDevice([...compressedFiles]));
      }
      dispatch(qrCodeActions.setUploadedPhotosFromDevice([...uploadedPhotosFromDevice, ...urls]));
    } else {
      toast.error("Максимальний розмір файлів - 20МБ", {id: "qr-code", duration: 5000});
    }
  };

  const handleDeleteSelectedPhoto = (event, index) => {
    // don`t overlap on parent div (opening modal window)
    event.stopPropagation();

    const updatedPhotos = [...uploadedPhotosFromDevice];
    const updatedFiles = [...compressedFilesFromDevice];
    updatedPhotos.splice(index, 1);
    updatedFiles.splice(index, 1);
    dispatch(qrCodeActions.setUploadedPhotosFromDevice(updatedPhotos));
    dispatch(qrCodeActions.setCompressedFilesFromDevice(updatedFiles));
  };

  useEffect(() => {
    if (compressedFilesFromDevice?.length > 0) {
      if (!(compressedFilesFromDevice[0] instanceof File)) {
        dispatch(qrCodeActions.setUploadedPhotosFromDevice([]));
        dispatch(qrCodeActions.setCompressedFilesFromDevice([]));
      }
    }
  }, []);

  const handleSelectPhotoForPreview = (file) => {
    dispatch(modalPreviewSelectedPhotoActions.openPreview('QrCodePhotos'));
    setPhotoForPreview(file.url);
  };

  const handleCreateFormData = (photos, session_id) => {
    let formData = new FormData();

    for (const photo of photos) {
      formData.append('photos_data', photo);
    }

    formData.append('session_id', session_id);

    return formData;
  }

  const handleAddPhotosByQr = async () => {
    setIsLoading(true);

    const formData = handleCreateFormData(compressedFilesFromDevice, sessionId);

    try {
      const res = await qrCodeService.addPhotos(sessionId, formData);

      if (res.status === 200) {
        alert(`Фото успішно надіслані! Щоб побачити їх на пристрої оновіть, будь ласка, вашу сторінку або вони з'являться автоматично через 3 секунди після відправки!`);
        navigate(MAIN_ROUTES.LOGIN);
      }
    } catch (error) {
      alert('На жаль, фото не були надіслані! Спробуйте, будь ласка, ще раз!');
      console.error(error);
    } finally {
      dispatch(qrCodeActions.setUploadedPhotosFromDevice([]));
      dispatch(qrCodeActions.setCompressedFilesFromDevice([]));
      setIsLoading(false);
    }
  }

  useEffect(() => {
    return () => {
      toast.remove("qr-code");
    }
  }, []);

  if (isPhotos) {
    return <ErrorPage/>;
  }

  return (
    isLoading ?
      <Loader/> :
      <div className={styles.addPhotoWrapper}>
        <div className={styles.addPhotoWrapper_description}>
          <p className={styles.addPhotoWrapper_description_title}>Додати фото забруднень</p>
          <p className={styles.addPhotoWrapper_description_subtitle}>Додайте фото вибравши з вашої
            галереї.</p>
        </div>

        <div className={styles.addPhotoWrapper_addPhotoContainer}>
          <div className={styles.addPhotoWrapper_addPhotoContainer_buttons}>
            <input
              onChange={handleGetPhotosFromInput}
              multiple
              type="file"
              id="fileInput"
              accept={'image/*'}
              style={{display: 'none'}}
            />

            <div className={styles.addPhotoWrapper_addPhotoContainer_buttons_add}>
              {uploadedPhotosFromDevice?.map((file, index) =>
                <div
                  key={index}
                  className={styles.add}
                  onClick={() => handleSelectPhotoForPreview(file)}
                  style={{backgroundImage: `url(${file?.url})`}}
                >
                  <div onClick={(event) => handleDeleteSelectedPhoto(event, index)}>
                    <img src={trashIcon} alt="trash-icon"/>
                  </div>
                </div>
              )}

              {(!!uploadedPhotosFromDevice.length < 3) ?
                (Array.from({length: 3 - uploadedPhotosFromDevice?.length})?.map((_, index) => (
                    <button
                      key={index}
                      onClick={handleAddPhoto}
                      className={styles.add}
                    >+</button>
                  ))
                ) : null}

            </div>

            <button
              onClick={handleAddPhoto}
              className={styles.addFullWidth}
            >Додати фото
            </button>

            <MainButtonWithBackground
              onClick={handleAddPhotosByQr}
              type={'button'}
              text={'Завантажити Фото'}
              width={'100%'}
              condition={compressedFilesFromDevice?.length === 0}
            />
          </div>

          {isPreviewOpen &&
            <ModalPreviewSelectedPhoto
              photo={photoForPreview}
              component={'QrCodePhotos'}
            />}

          <Toaster/>
        </div>
      </div>
  );
};

export default QrCodePhotos;
